/**
 * Private Booking Widget Page
 *
 * Flow when a teacherId is pre-configured via data-teacher-id:
 *   1. calendar-select — weekly grid: pick a day + time slot in one step
 *   2. guest-form      — enter name + email
 *   3. confirmed       — booking confirmation screen
 *
 * Flow without a pre-configured teacher (shows teacher list first):
 *   1. teacher-select  — pick a teacher
 *   2. calendar-select — weekly grid
 *   3. guest-form
 *   4. confirmed
 */
import { useState, useEffect, useMemo } from "react";
import {
  usePrivateBookingStore,
  type PrivateTeacher,
  type PrivateSlot,
} from "../../store/private-booking-store";
import Loader from "../loader";
import { useStudioStore } from "@/store/studio-store";
import { useViewStore } from "@/store/view-store";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale, useTranslations } from "../../../i18n";

// ── Tiny API helper (no-auth) ─────────────────────────────────────────────────

async function publicFetch(url: string, options?: RequestInit) {
  const res = await fetch(url, { ...options });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).error ?? `API error ${res.status}`);
  }
  return res.json();
}

// ── Date helpers ─────────────────────────────────────────────────────────────

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatLocalDate(dateStr: string, locale: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function PrivateBookingPage() {
  const { step, init, reset, selectTeacher } = usePrivateBookingStore();
  const selectedTeacher = usePrivateBookingStore((s) => s.selectedTeacher);
  const { studio } = useStudioStore();
  const { navigate, previous } = useViewStore();
  const locale = useLocale();

  const params = new URLSearchParams(window.location.search);
  const presetTeacherId = params.get("teacherId");

  const studioSlug = studio?.slug;

  // Initialise flow on mount
  useEffect(() => {
    init(presetTeacherId ?? undefined);

    // If teacher is pre-configured, enrich with actual teacher data in the background
    if (presetTeacherId && studioSlug) {
      publicFetch(`/api/public/studios/${studioSlug}/private-booking/teachers`)
        .then((teachers: PrivateTeacher[]) => {
          const found = teachers.find((t) => t.id === presetTeacherId);
          if (found) {
            usePrivateBookingStore.setState({ selectedTeacher: found });
          }
        })
        .catch(() => {
          // keep the minimal teacher object set by init()
        });
    }
  }, []);

  if (!studioSlug) return null;

  const teacherId = selectedTeacher?.id ?? presetTeacherId ?? "";

  return (
    <section className="w-full min-h-[400px] p-4">
      <div className="max-w-3xl mx-auto">
        {step === "teacher-select" && (
          <TeacherSelectStep
            studioSlug={studioSlug}
            onBack={() => navigate(previous ?? "calendar")}
            onSelect={selectTeacher}
          />
        )}
        {step === "calendar-select" && teacherId && (
          <WeeklyCalendarStep
            studioSlug={studioSlug}
            teacherId={teacherId}
            locale={locale}
          />
        )}
        {step === "guest-form" && (
          <GuestFormStep studioSlug={studioSlug} locale={locale} />
        )}
        {step === "confirmed" && (
          <ConfirmedStep onReset={reset} locale={locale} />
        )}
      </div>
    </section>
  );
}

// ── Step 1: Teacher select ────────────────────────────────────────────────────

function TeacherSelectStep({
  studioSlug,
  onBack,
  onSelect,
}: {
  studioSlug: string;
  onBack: () => void;
  onSelect: (t: PrivateTeacher) => void;
}) {
  const t = useTranslations("privateBooking");
  const [teachers, setTeachers] = useState<PrivateTeacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    publicFetch(`/api/public/studios/${studioSlug}/private-booking/teachers`)
      .then(setTeachers)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [studioSlug]);

  if (loading) return <CenteredLoader />;
  if (error) return <ErrorMsg msg={error} />;
  if (teachers.length === 0) return <EmptyState msg={t("noTeachers")} />;

  return (
    <div>
      <BackButton onClick={onBack} />
      <StepHeader
        title={t("bookPrivateSession")}
        subtitle={t("chooseTeacher")}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {teachers.map((teacher) => (
          <button
            key={teacher.id}
            onClick={() => onSelect(teacher)}
            className="flex items-start gap-3 rounded-2xl border bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md hover:border-primary"
          >
            <Avatar name={teacher.name} src={teacher.avatar} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-foreground truncate">
                {teacher.name ?? t("teacherFallback")}
              </p>
              {teacher.bio && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                  {teacher.bio}
                </p>
              )}
              <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                <span>⏱ {teacher.sessionDurationMin} min</span>
                {teacher.pricePerSession != null && (
                  <span>
                    {new Intl.NumberFormat(undefined, {
                      style: "currency",
                      currency: teacher.currency,
                    }).format(teacher.pricePerSession / 100)}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 2: Weekly calendar (date + slot combined) ────────────────────────────

const DAYS_SHOWN = 8;

function WeeklyCalendarStep({
  studioSlug,
  teacherId,
  locale,
}: {
  studioSlug: string;
  teacherId: string;
  locale: string;
}) {
  const tr = useTranslations("privateBooking");
  const [weekOffset, setWeekOffset] = useState(0);
  const [slotsByDay, setSlotsByDay] = useState<Record<string, PrivateSlot[]>>(
    {},
  );

  const days = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return Array.from({ length: DAYS_SHOWN }, (_, i) => {
      const d = new Date(t);
      d.setDate(t.getDate() + weekOffset * DAYS_SHOWN + i);
      return d;
    });
  }, [weekOffset]);

  // Derive loading: if the first day of the current window isn't in slotsByDay yet, we're loading
  const loading = !(toDateStr(days[0]) in slotsByDay);

  useEffect(() => {
    let cancelled = false;

    Promise.all(
      days.map((d) =>
        publicFetch(
          `/api/public/studios/${studioSlug}/private-booking/teachers/${teacherId}/slots?date=${toDateStr(d)}`,
        )
          .then((data) => ({
            date: toDateStr(d),
            slots: (data.slots ?? []) as PrivateSlot[],
          }))
          .catch(() => ({ date: toDateStr(d), slots: [] as PrivateSlot[] })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const map: Record<string, PrivateSlot[]> = {};
      results.forEach((r) => {
        map[r.date] = r.slots;
      });
      setSlotsByDay(map);
    });

    return () => {
      cancelled = true;
    };
  }, [days, studioSlug, teacherId]);

  function handleSlotClick(date: string, slot: PrivateSlot) {
    usePrivateBookingStore.setState({
      selectedDate: date,
      selectedSlot: slot,
      step: "guest-form",
    });
  }

  const maxRows = loading
    ? 3
    : Math.max(...days.map((d) => slotsByDay[toDateStr(d)]?.length ?? 0), 0);

  return (
    <div>
      {/* Title + navigation */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-base font-semibold text-foreground">
          {tr("chooseSlot")}
        </h2>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setWeekOffset((w) => Math.max(0, w - 1))}
            disabled={weekOffset === 0}
            aria-label={tr("previousPeriod")}
            className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => setWeekOffset((w) => w + 1)}
            aria-label={tr("nextPeriod")}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Day headers */}
      <div
        className="grid gap-1 pb-3 border-b border-border/50"
        style={{ gridTemplateColumns: `repeat(${DAYS_SHOWN}, 1fr)` }}
      >
        {days.map((d) => (
          <div key={toDateStr(d)} className="text-center">
            <div className="text-xs font-semibold text-foreground capitalize">
              {d
                .toLocaleDateString(locale, { weekday: "short" })
                .replace(/\.$/, "") + "."}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {d.getDate()}{" "}
              {d
                .toLocaleDateString(locale, { month: "short" })
                .replace(/\.$/, "") + "."}
            </div>
          </div>
        ))}
      </div>

      {/* Slot rows */}
      <div className="mt-3 space-y-2">
        {loading ? (
          Array.from({ length: 3 }, (_, rowIdx) => (
            <div
              key={rowIdx}
              className="grid gap-1"
              style={{ gridTemplateColumns: `repeat(${DAYS_SHOWN}, 1fr)` }}
            >
              {days.map((d) => (
                <div
                  key={toDateStr(d)}
                  className="h-9 rounded-xl bg-muted/60 animate-pulse"
                />
              ))}
            </div>
          ))
        ) : maxRows === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {tr("noSlotsThisWeek")}
          </div>
        ) : (
          Array.from({ length: maxRows }, (_, rowIdx) => (
            <div
              key={rowIdx}
              className="grid gap-1"
              style={{ gridTemplateColumns: `repeat(${DAYS_SHOWN}, 1fr)` }}
            >
              {days.map((d) => {
                const slot = slotsByDay[toDateStr(d)]?.[rowIdx];
                return slot ? (
                  <button
                    key={slot.startAt}
                    onClick={() => handleSlotClick(toDateStr(d), slot)}
                    className="rounded-xl bg-primary text-white text-xs font-semibold py-2.5 text-center hover:opacity-90 active:scale-95 transition-all"
                  >
                    {slot.localStart}
                  </button>
                ) : (
                  <div
                    key={`${toDateStr(d)}-empty-${rowIdx}`}
                    className="flex items-center justify-center py-2.5 text-muted-foreground/30 text-sm select-none"
                  >
                    —
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Step 3: Guest form ────────────────────────────────────────────────────────

function GuestFormStep({
  studioSlug,
  locale,
}: {
  studioSlug: string;
  locale: string;
}) {
  const t = useTranslations("privateBooking");
  const { selectedTeacher, selectedDate, selectedSlot, setConfirmed } =
    usePrivateBookingStore();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedTeacher || !selectedSlot) return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await publicFetch(
        `/api/public/studios/${studioSlug}/private-booking/teachers/${selectedTeacher.id}/book`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startAt: selectedSlot.startAt,
            guestName: name.trim(),
            guestEmail: email.trim(),
          }),
        },
      );
      setConfirmed(data.bookingId);
    } catch (err: any) {
      setError(err.message ?? t("somethingWentWrong"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <BackButton
        onClick={() =>
          usePrivateBookingStore.setState({
            step: "calendar-select",
            selectedSlot: null,
          })
        }
      />
      <StepHeader
        title={t("yourDetails")}
        subtitle={
          selectedDate && selectedSlot
            ? `${formatLocalDate(selectedDate, locale)} ${t("at")} ${selectedSlot.localStart}${selectedTeacher?.name ? ` ${t("with")} ${selectedTeacher.name}` : ""}`
            : ""
        }
      />

      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("fullName")}
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-xl border border-border px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            placeholder="Jane Doe"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("emailAddress")}
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-border px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            placeholder="jane@example.com"
          />
        </div>

        {error && (
          <p className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !name.trim() || !email.trim()}
          className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
        >
          {submitting ? t("bookingInProgress") : t("confirmBooking")}
        </button>
      </form>
    </div>
  );
}

// ── Step 4: Confirmed ─────────────────────────────────────────────────────────

function ConfirmedStep({
  onReset,
  locale,
}: {
  onReset: () => void;
  locale: string;
}) {
  const t = useTranslations("privateBooking");
  const { selectedTeacher, selectedDate, selectedSlot } =
    usePrivateBookingStore();

  return (
    <div className="text-center py-10">
      <div className="text-5xl mb-4">✅</div>
      <h2 className="text-xl font-bold mb-2">{t("youreBooked")}</h2>
      {selectedTeacher?.name && (
        <p className="text-sm text-muted-foreground mb-1">
          {t("sessionWith")} <strong>{selectedTeacher.name}</strong>{" "}
          {t("isConfirmed")}
        </p>
      )}
      {selectedDate && selectedSlot && (
        <p className="text-sm text-muted-foreground mb-6">
          {formatLocalDate(selectedDate, locale)} {t("at")}{" "}
          {selectedSlot.localStart} – {selectedSlot.localEnd}
        </p>
      )}
      <p className="text-xs text-muted-foreground mb-8">
        {t("confirmationSentToEmail")}
      </p>
      <button
        onClick={onReset}
        className="rounded-xl border border-primary px-6 py-2.5 text-sm font-medium text-primary hover:bg-primary hover:text-white transition-colors"
      >
        {t("bookAnotherSession")}
      </button>
    </div>
  );
}

// ── Shared micro-components ───────────────────────────────────────────────────

function StepHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-6 text-center">
      <h2 className="text-xl font-bold text-foreground">{title}</h2>
      {subtitle && (
        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
      )}
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  const t = useTranslations("privateBooking");
  return (
    <button
      onClick={onClick}
      className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
    >
      {t("back")}
    </button>
  );
}

function Avatar({ name, src }: { name: string | null; src: string | null }) {
  const initials = name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  if (src) {
    return (
      <img
        src={src}
        alt={name ?? ""}
        className="w-12 h-12 rounded-full object-cover shrink-0"
      />
    );
  }

  return (
    <div className="w-12 h-12 rounded-full bg-primary/10 text-primary font-semibold text-sm flex items-center justify-center shrink-0">
      {initials}
    </div>
  );
}

function CenteredLoader() {
  return (
    <div className="flex justify-center py-16">
      <Loader />
    </div>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return (
    <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
      {msg}
    </div>
  );
}

function EmptyState({ msg }: { msg: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-3 text-3xl">📅</div>
      <p className="text-sm text-muted-foreground">{msg}</p>
    </div>
  );
}
